import { useState, useEffect } from 'react';
import axios from 'axios';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CourseCard } from '../components/CourseCard';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/api/courses.php?action=list`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name.toLowerCase().includes(search.toLowerCase()) ||
                          course.description.toLowerCase().includes(search.toLowerCase());
    const matchesLevel = levelFilter === 'all' || course.level === levelFilter;
    return matchesSearch && matchesLevel;
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="pt-32 pb-16 px-6" data-testid="courses-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-fadeIn">
            <p className="text-xs uppercase tracking-widest text-primary mb-4">
              Kursplan
            </p>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Unsere <span className="text-primary">Kurse</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Entdecken Sie unser Angebot an Tango Argentino Kursen. 
              Für jeden Level und jedes Ziel das Richtige.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Kurse durchsuchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-secondary border-border"
                data-testid="course-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={levelFilter} onValueChange={setLevelFilter}>
                <SelectTrigger className="w-48 bg-secondary border-border" data-testid="level-filter">
                  <SelectValue placeholder="Alle Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Level</SelectItem>
                  <SelectItem value="Anfänger" data-testid="filter-anfaenger">Anfänger</SelectItem>
                  <SelectItem value="Mittelstufe" data-testid="filter-mittelstufe">Mittelstufe</SelectItem>
                  <SelectItem value="Fortgeschritten" data-testid="filter-fortgeschritten">Fortgeschritten</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Course Grid */}
          {loading ? (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-4 text-muted-foreground">Kurse werden geladen...</p>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground">Keine Kurse gefunden.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCourses.map((course, index) => (
                <div 
                  key={course.id} 
                  className="animate-fadeIn" 
                  style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                >
                  <CourseCard course={course} />
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
